/**
 * <ny-scene> — product hotspots for the NY "Shop The Scene" section.
 * Vanilla custom element, no dependencies.
 * - Desktop (hover-capable, ≥750px): hover or click opens a card beside the dot,
 *   clamped inside the image so it never leaves the stage.
 * - Touch / mobile: tap opens a fixed bottom card that always fits the viewport.
 * - Keyboard: Enter/Space toggles, Escape closes and returns focus to the dot.
 * - Theme editor: selecting a hotspot block opens its card.
 */
const desktopQuery = window.matchMedia('(min-width: 750px)');
const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

class NyScene extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {HTMLElement | null} */
  #openCard = null;
  /** @type {HTMLButtonElement | null} */
  #openDot = null;
  /** @type {number | undefined} */
  #hoverTimer;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** True when the card was opened by click/tap/keyboard (not hover). */
  #pinned = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.querySelectorAll('[data-ny-dot]').forEach((dot) => {
      if (!(dot instanceof HTMLButtonElement)) return;
      const spot = dot.closest('.ny-scene__spot');

      dot.addEventListener(
        'click',
        (event) => {
          const card = this.#cardFor(dot);
          if (!card) return;
          window.clearTimeout(this.#hoverTimer);
          if (this.#openDot === dot && this.#pinned === false && event.detail !== 0) {
            // Opened by hover: a mouse click pins it open instead of closing it.
            this.#pinned = true;
          } else if (this.#openDot === dot) {
            this.close(true);
          } else {
            // Keyboard activation (detail === 0) or touch: move focus into the card.
            const moveFocus = event.detail === 0 || !this.#canHover();
            this.open(dot, moveFocus);
          }
        },
        { signal }
      );

      spot?.addEventListener('pointerenter', () => this.#hoverOpen(dot), { signal });
      spot?.addEventListener('pointerleave', () => this.#hoverClose(), { signal });
    });

    this.querySelectorAll('.ny-scene__card').forEach((card) => {
      card.addEventListener('pointerenter', () => window.clearTimeout(this.#hoverTimer), { signal });
      card.addEventListener('pointerleave', () => this.#hoverClose(), { signal });
      card.querySelector('[data-ny-close]')?.addEventListener('click', () => this.close(true), { signal });
      // Keyboard users tabbing out of the card close it (focus moving into a theme
      // modal such as quick add does not count).
      card.addEventListener(
        'focusout',
        (event) => {
          const next = /** @type {FocusEvent} */ (event).relatedTarget;
          if (!(next instanceof Element) || card !== this.#openCard) return;
          if (card.contains(next) || next === this.#openDot || next.closest('dialog')) return;
          this.close(false);
        },
        { signal }
      );
    });

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.#openCard) {
          // Let an open theme modal (quick add) handle its own Escape first.
          if (document.querySelector('dialog[open]')) return;
          this.close(true);
        }
      },
      { signal }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!this.#openCard) return;
        const target = /** @type {Node} */ (event.target);
        if (target instanceof Element && target.closest('dialog')) return;
        if (!this.contains(target)) this.close(false);
        else if (!this.#openCard.contains(target) && !(target instanceof Element && target.closest('[data-ny-dot]')))
          this.close(false);
      },
      { signal }
    );

    window.addEventListener('resize', () => this.#position(), { signal, passive: true });

    // The mobile card is fixed to the viewport; close it once the scene scrolls away
    // so it never lingers over unrelated content.
    const stage = this.querySelector('.ny-scene__stage');
    if (stage && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => !entry.isIntersecting) && this.#openCard && !desktopQuery.matches) {
          this.close(false);
        }
      });
      this.#observer.observe(stage);
    }
    desktopQuery.addEventListener('change', () => this.#position(), { signal });

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        const spot = this.querySelector(`.ny-scene__spot[data-block-id="${detail?.blockId}"]`);
        const dot = spot?.querySelector('[data-ny-dot]');
        if (dot instanceof HTMLButtonElement) this.open(dot, false);
      },
      { signal }
    );

    document.addEventListener('shopify:block:deselect', () => this.close(false), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#observer = null;
    window.clearTimeout(this.#hoverTimer);
  }

  #canHover() {
    return hoverQuery.matches && desktopQuery.matches;
  }

  /** @param {HTMLButtonElement} dot */
  #cardFor(dot) {
    const id = dot.getAttribute('aria-controls');
    return id ? /** @type {HTMLElement | null} */ (this.querySelector(`#${CSS.escape(id)}`)) : null;
  }

  /** @param {HTMLButtonElement} dot */
  #hoverOpen(dot) {
    if (!this.#canHover()) return;
    window.clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => {
      if (this.#openDot !== dot) {
        this.open(dot, false);
        this.#pinned = false;
      }
    }, 90);
  }

  #hoverClose() {
    if (!this.#canHover()) return;
    window.clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => {
      const card = this.#openCard;
      if (!card || this.#pinned) return;
      // Keep open while focus is inside (keyboard users) or a theme modal is open.
      if (card.contains(document.activeElement) || document.querySelector('dialog[open]')) return;
      this.close(false);
    }, 260);
  }

  /**
   * @param {HTMLButtonElement} dot
   * @param {boolean} moveFocus
   */
  open(dot, moveFocus) {
    const card = this.#cardFor(dot);
    if (!card) return;
    if (this.#openDot && this.#openDot !== dot) this.close(false);

    card.hidden = false;
    dot.setAttribute('aria-expanded', 'true');
    this.#openCard = card;
    this.#openDot = dot;
    this.#pinned = true;
    this.#position();

    if (moveFocus) {
      const focusTarget = card.querySelector('a[href]:not([tabindex="-1"]), button:not([disabled])');
      if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    }
  }

  /** @param {boolean} returnFocus */
  close(returnFocus) {
    const card = this.#openCard;
    const dot = this.#openDot;
    if (!card || !dot) return;

    card.hidden = true;
    card.style.removeProperty('top');
    card.style.removeProperty('left');
    dot.setAttribute('aria-expanded', 'false');
    this.#openCard = null;
    this.#openDot = null;

    if (returnFocus) dot.focus({ preventScroll: true });
  }

  #position() {
    const card = this.#openCard;
    const dot = this.#openDot;
    if (!card || !dot) return;

    if (!desktopQuery.matches) {
      // Mobile: CSS pins the card to the bottom of the viewport.
      card.style.removeProperty('top');
      card.style.removeProperty('left');
      return;
    }

    const stage = card.parentElement;
    if (!stage) return;

    const GAP = 14;
    const EDGE = 8;
    const stageRect = stage.getBoundingClientRect();
    const dotRect = dot.getBoundingClientRect();
    const cardWidth = card.offsetWidth;
    const cardHeight = card.offsetHeight;
    const rtl = getComputedStyle(this).direction === 'rtl';

    const dotLeft = dotRect.left - stageRect.left;
    const dotRight = dotRect.right - stageRect.left;
    const dotMidY = dotRect.top - stageRect.top + dotRect.height / 2;

    const rightSide = dotRight + GAP;
    const leftSide = dotLeft - GAP - cardWidth;
    const fitsRight = rightSide + cardWidth <= stageRect.width - EDGE;
    const fitsLeft = leftSide >= EDGE;

    let left = rtl ? (fitsLeft ? leftSide : rightSide) : fitsRight ? rightSide : leftSide;
    if (!fitsRight && !fitsLeft) left = dotLeft + dotRect.width / 2 - cardWidth / 2;
    left = Math.min(Math.max(left, EDGE), Math.max(EDGE, stageRect.width - cardWidth - EDGE));

    let top = dotMidY - cardHeight / 2;
    top = Math.min(Math.max(top, EDGE), Math.max(EDGE, stageRect.height - cardHeight - EDGE));

    // Card is positioned with physical left for measurement simplicity.
    card.style.left = `${Math.round(left)}px`;
    card.style.top = `${Math.round(top)}px`;
  }
}

if (!customElements.get('ny-scene')) {
  customElements.define('ny-scene', NyScene);
}
